import {
    Client,
    BatchRequestStep,
    BatchRequestContent,
    BatchResponseContent
} from "@microsoft/microsoft-graph-client/lib/src/browser";
import { delay } from "../../../utilities/FetchRetryUtil";

const RETRYABLE_BATCH_STATUS_CODES: number[] = [429, 502, 503, 504];
const MAX_BATCH_ATTEMPTS: number = 3;
const MAX_BATCH_RETRY_DELAY_MS: number = 10000;
const BATCH_SIZE: number = 20;

/** Graph's Outlook connector allows 4 concurrent requests per mailbox. */
export const GRAPH_MAILBOX_CONCURRENCY: number = 4;

/**
 * True for statuses a later retry can plausibly cure (throttling, transient
 * gateway errors). Callers use this to decide whether an individual-request
 * fallback after a failed batch step is worth issuing at all — a 403/404
 * would fail identically outside the batch.
 */
export const isRetryableBatchStatus = (status: number): boolean => RETRYABLE_BATCH_STATUS_CODES.includes(status);

export interface IBatchStepResult {
    ok: boolean;
    status: number;
    body?: any;
}

export interface IBatchResult {
    /** false when at least one step still failed with a retryable status after all attempts */
    complete: boolean;
    stepResults: Map<string, IBatchStepResult>;
}

/**
 * Executes Graph $batch steps with retry for throttled sub-responses.
 * Graph evaluates each step in a batch individually against the Outlook
 * concurrency limit (4 per mailbox) and the SDK does NOT retry throttled
 * steps inside a batch, so 429s must be retried here, honoring Retry-After.
 * Batches run one at a time to stay within the per-mailbox limit.
 */
export const executeBatchWithRetry = async (client: Client, steps: BatchRequestStep[]): Promise<IBatchResult> => {
    const stepResults = new Map<string, IBatchStepResult>();
    let pendingSteps: BatchRequestStep[] = steps;

    for (let attempt = 1; pendingSteps.length > 0 && attempt <= MAX_BATCH_ATTEMPTS; attempt++) {
        const throttledSteps: BatchRequestStep[] = [];
        let retryDelayMs: number = 0;

        for (let i = 0; i < pendingSteps.length; i += BATCH_SIZE) {
            const batchSteps = pendingSteps.slice(i, i + BATCH_SIZE);

            const content = await new BatchRequestContent(batchSteps).getContent();
            const batchResponse = await client.api("/$batch").post(content);
            const batchResponseContent = new BatchResponseContent(batchResponse);

            for (const step of batchSteps) {
                const stepResponse = batchResponseContent.getResponseById(step.id);
                if (!stepResponse) {
                    stepResults.set(step.id, { ok: false, status: 0 });
                }
                else if (stepResponse.ok) {
                    stepResults.set(step.id, { ok: true, status: stepResponse.status, body: await stepResponse.json() });
                }
                else if (RETRYABLE_BATCH_STATUS_CODES.includes(stepResponse.status) && attempt < MAX_BATCH_ATTEMPTS) {
                    throttledSteps.push(step);
                    // Number(null) is 0, not NaN — only parse a present, non-empty header,
                    // otherwise the attempt-scaled fallback backoff would never apply.
                    const retryAfterHeader = stepResponse.headers?.get("Retry-After");
                    const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) : NaN;
                    const stepDelayMs = isNaN(retryAfterSeconds) ? attempt * 1000 : retryAfterSeconds * 1000;
                    retryDelayMs = Math.max(retryDelayMs, stepDelayMs);
                }
                else {
                    stepResults.set(step.id, { ok: false, status: stepResponse.status });
                }
            }
        }

        if (throttledSteps.length > 0) {
            await delay(Math.min(retryDelayMs, MAX_BATCH_RETRY_DELAY_MS));
        }
        pendingSteps = throttledSteps;
    }

    const complete = !Array.from(stepResults.values())
        .some(result => !result.ok && (result.status === 0 || RETRYABLE_BATCH_STATUS_CODES.includes(result.status)));

    return { complete, stepResults };
};
